import useForkRef from '@mui/utils/useForkRef';
import { useLongPress } from '@unifyapps/hooks/useLongPress';
import type React from 'react';
import { Children, cloneElement, forwardRef, isValidElement, useMemo } from 'react';
import { slowCn } from '../../lib/utils';
import { usePopoverContext } from './PopoverContext';
import type { PopoverButtonProps } from './types';

const getAnchorFromChildren = (children: React.ReactNode) => {
  const childArray = Children.toArray(children);

  if (childArray.length !== 1) {
    console.error(
      `[PopoverButton] Exactly 1 child must be passed to Popover, found ${childArray.length} children`,
    );
  }

  return childArray[0];
};

/**
 * Delay in ms before resetting the longPressJustOpened flag.
 * This allows time for the synthetic click to propagate through ClickAwayListeners
 * which may have async event handling (e.g., MUI ClickAwayListener).
 */
const LONG_PRESS_FLAG_RESET_DELAY_MS = 50;

// Reference: https://github.com/uber/baseweb/blob/master/src/popover/popover.tsx#L295
const PopoverButton = forwardRef<HTMLElement, PopoverButtonProps>(
  function PopoverButton(props, forwardedRef) {
    const {
      children,
      classes,
      className,
      disabled,
      stopPropagation = true,
      toggleOnClick = false,
      ...rest
    } = props;

    const { onOpenChange, open, setAnchorEl, triggerType, onMouseMove, longPressJustOpenedRef } =
      usePopoverContext();
    const ref = useForkRef(forwardedRef, setAnchorEl);
    const anchor = getAnchorFromChildren(
      typeof children === 'function' ? children({ open }) : children,
    );

    /**
     * Handles long press completion for popover trigger.
     * Fires a synthetic click event because useLongPress blocks click events after a long press (see useLongPress/index.ts:86-115)
     * so other popovers' ClickAwayListeners need this synthetic click to close.
     * The current popover ignores it via longPressJustOpenedRef flag.
     */
    const handleLongPressComplete = useMemo(() => {
      if (triggerType !== 'longPress' || disabled || !longPressJustOpenedRef) return null;

      return () => {
        // set flag BEFORE opening to ensure it's set when synthetic click fires
        // this ensures the current popover ignores the synthetic click
        longPressJustOpenedRef.current = true;
        onOpenChange(true);

        // fire a synthetic click event on document body after yielding to the event loop
        // this allows other popovers' ClickAwayListeners to detect it and close
        setTimeout(() => {
          const syntheticClick = new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
          });

          document.body.dispatchEvent(syntheticClick);

          // reset flag after allowing time for async event handling in ClickAwayListeners
          setTimeout(() => {
            longPressJustOpenedRef.current = false;
          }, LONG_PRESS_FLAG_RESET_DELAY_MS);
        }, 0);
      };
    }, [triggerType, disabled, onOpenChange, longPressJustOpenedRef]);

    const { longPressProps } = useLongPress(handleLongPressComplete);

    const anchorProps = useMemo(() => {
      const anchorElement = anchor as null | React.ReactElement<{
        onClick?: (event: React.MouseEvent) => void;
        className?: string;
        [key: string]: unknown;
      }>;

      if (!anchorElement) {
        return {};
      }

      const onClick = (event: React.MouseEvent) => {
        if (stopPropagation) {
          event.preventDefault();
          event.stopPropagation();
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access -- this is a generic component, so unsafe access is necessary
        anchorElement.props?.onClick?.(event);
        if (triggerType === 'click') {
          // Default stays open-only (close is the content's click-away job). Opt-in toggleOnClick
          // makes the trigger close itself, for cases where stopPropagation above kills the
          // bubble-phase click-away on a trigger re-click (e.g. a filter popover inside a Modal).
          onOpenChange(toggleOnClick ? !open : true);
        }
      };

      const anchorClassName = slowCn(
        className,
        classes?.root,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access -- this is a generic component, so unsafe access is necessary
        anchorElement.props?.className,
        disabled && classes?.disabled,
        open ? classes?.open : classes?.closed,
        { 'cursor-not-allowed': disabled, open },
      );

      const baseProps = {
        role: 'button',
        'aria-expanded': Boolean(open),
        'aria-pressed': Boolean(open),
        'aria-haspopup': true,
        className: anchorClassName,
        disabled,
        ref,
        onMouseEnter: onMouseMove,
        onMouseLeave: onMouseMove,
        onClick: disabled ? undefined : onClick,
      };

      // when using longPress trigger, merge longPress props (onClick still works for regular clicks)
      if (triggerType === 'longPress') {
        return {
          ...baseProps,
          ...longPressProps,
        };
      }

      return baseProps;
    }, [
      anchor,
      className,
      classes?.closed,
      classes?.disabled,
      classes?.open,
      classes?.root,
      disabled,
      longPressProps,
      onMouseMove,
      onOpenChange,
      open,
      ref,
      stopPropagation,
      toggleOnClick,
      triggerType,
    ]);

    if (!anchor) {
      return null;
    }

    if (typeof anchor === 'object' && isValidElement(anchor)) {
      return cloneElement(anchor, { ...rest, ...anchorProps });
    }

    return (
      <span {...rest} {...anchorProps}>
        {anchor}
      </span>
    );
  },
);

export type { PopoverButtonProps };
export default PopoverButton;
